/** FOUNDER-DASHBOARD-IMPLEMENTATION-000007 — India Business Intelligence Engine + Warehouses. */

import type { WarehouseMapPin, WarehouseMapRow } from './warehouseLayer';

export type MapLayerId = 'sales' | 'orders' | 'units';

/** Overlay layers (toggle independent of heat). LIVE from Warehouse Master. */
export type WarehouseOverlayId = 'warehouse_locations' | 'warehouse_coverage';

/** Reserved / CERT-gated layers — UI disabled only. */
export type FutureMapLayerId =
  | 'returns'
  | 'inventory'
  | 'delivery'
  | 'business_health'
  | 'traffic'
  | 'advertising'
  | 'brand_analytics';

export type IndiaStateCode =
  | 'AN'
  | 'AP'
  | 'AR'
  | 'AS'
  | 'BR'
  | 'CH'
  | 'CT'
  | 'DH'
  | 'DL'
  | 'GA'
  | 'GJ'
  | 'HP'
  | 'HR'
  | 'JH'
  | 'JK'
  | 'KA'
  | 'KL'
  | 'LA'
  | 'LD'
  | 'MH'
  | 'ML'
  | 'MN'
  | 'MP'
  | 'MZ'
  | 'NL'
  | 'OD'
  | 'PB'
  | 'PY'
  | 'RJ'
  | 'SK'
  | 'TG'
  | 'TN'
  | 'TR'
  | 'UP'
  | 'UT'
  | 'WB';

export type DayPoint = {
  iso: string;
  gmv: number | null;
  orders: number;
  units: number | null;
};

export type ProductRow = {
  key: string;
  orders: number;
  units: number | null;
  gmv: number | null;
  contributionPct: number | null;
};

export type CityRow = {
  city: string;
  state?: string;
  orders: number;
  units?: number | null;
  gmv: number | null;
  contributionPct: number | null;
};

export type InsightItem = {
  id: string;
  tone: 'positive' | 'watch' | 'info';
  text: string;
};

export type StateAggregate = {
  code: IndiaStateCode;
  name: string;
  orderCount: number;
  ordersWithTotal: number;
  gmv: number | null;
  units: number | null;
  ordersWithUnits: number;
  currencies: string[];
  /** Share of geo-known GMV (or orders when GMV multi-currency). */
  contributionPct: number | null;
  shareOfKnownGeoPct: number | null;
  /** 1 = highest among states with LIVE data; null if no data. */
  nationalRank: number | null;
  aov: number | null;
  asp: number | null;
  /** GMV change vs compare window for this state; null if unavailable. */
  growthPct: number | null;
  lastUpdated: string | null;
  lastSync: string | null;
  /** % of this state's orders with parseable total. */
  coveragePct: number | null;
  confidence: 'high' | 'medium' | 'low' | 'none';
  topProducts: ProductRow[];
  topCities: CityRow[];
  trend7: DayPoint[];
  trend15: DayPoint[];
  trend30: DayPoint[];
  hasData: boolean;
};

export type IndiaMapModel = {
  connectionId: string;
  sellerLabel: string;
  marketplace: string;
  periodLabel: string;
  compareLabel: string | null;
  periodFrom: string;
  periodTo: string;
  asOf: string;
  truncated: boolean;
  totalOrdersInPeriod: number;
  ordersWithShipState: number;
  addressCoveragePct: number | null;
  heatGated: boolean;
  gateReason: string | null;
  multiCurrency: boolean;
  totalGmvKnownGeo: number | null;
  totalOrdersKnownGeo: number;
  totalUnitsKnownGeo: number | null;
  currency: string | undefined;
  states: StateAggregate[];
  ranked: IndiaStateCode[];
  /** National city ranking from All Orders ship-city. */
  topCities: CityRow[];
  source?: string;
  /** National deterministic insights from LIVE aggregates only. */
  insights: InsightItem[];
  /** Warehouse Master rows for tenant (from list API; never invented). */
  warehouses: WarehouseMapRow[];
  warehousePins: WarehouseMapPin[];
  warehouseCoverageStates: IndiaStateCode[];
  warehouseMasterTotal: number;
  warehouseMissingCoords: number;
  warehouseLoadError: string | null;
  error?: string;
};

export const LIVE_MAP_LAYERS: { id: MapLayerId; label: string }[] = [
  { id: 'sales', label: 'Sales heat' },
  { id: 'orders', label: 'Orders heat' },
  { id: 'units', label: 'Units heat' },
];

export const WAREHOUSE_OVERLAY_LAYERS: {
  id: WarehouseOverlayId;
  label: string;
}[] = [
  { id: 'warehouse_locations', label: 'Warehouse locations' },
  { id: 'warehouse_coverage', label: 'Warehouse coverage' },
];

export const FUTURE_MAP_LAYERS: {
  id: FutureMapLayerId;
  label: string;
  reason: string;
}[] = [
  { id: 'returns', label: 'Returns', reason: 'Available after certification' },
  { id: 'inventory', label: 'Inventory', reason: 'Available after certification' },
  {
    id: 'delivery',
    label: 'Delivery performance',
    reason: 'Available after certification',
  },
  {
    id: 'business_health',
    label: 'Business health',
    reason: 'Available after certification',
  },
  { id: 'traffic', label: 'Traffic', reason: 'Available after certification' },
  {
    id: 'advertising',
    label: 'Advertising',
    reason: 'Available after certification',
  },
  {
    id: 'brand_analytics',
    label: 'Brand analytics',
    reason: 'Available after certification',
  },
];
